/**
 * BotClearance.js
 *
 * Main container for the BOT Clearance page.
 * Implements a wizard-based flow for managing student clearances.
 *
 * Features:
 * - Default view: Table of students with clearance assignments
 * - Wizard flow: Student Info → Select Items → Review & Confirm → Payment
 * - Progress tracking with visual indicators
 * - Navigation rules based on clearance status
 *
 * @module BotClearance
 */
import React, { useState } from "react";
import { Card, Typography } from "antd";
import { ShoppingOutlined } from "@ant-design/icons";
import AssignmentsTable from "./components/AssignmentsTable";
import ClearanceWizard from "./components/ClearanceWizard";

const { Title, Text } = Typography;

/**
 * BotClearance Component
 *
 * Main container that switches between:
 * - List view: Shows all students with clearance assignments
 * - Wizard view: Multi-step clearance process for selected student
 *
 * @component
 */
const BotClearance = () => {
  /**
   * Current view mode: 'list' or 'wizard'
   */
  const [view, setView] = useState("list");

  /**
   * Selected clearance assignment for wizard view
   */
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  /**
   * Handle selection of an assignment from the table
   * Switches to wizard view with the selected assignment
   *
   * @param {object} assignment - The selected clearance assignment
   */
  const handleSelectAssignment = (assignment) => {
    setSelectedAssignment(assignment);
    setView("wizard");
  };

  /**
   * Handle returning to the list view from wizard
   * Clears selected assignment and switches view
   */
  const handleBackToList = () => {
    setSelectedAssignment(null);
    setView("list");
  };

  return (
    <div style={{ padding: "24px" }}>
      {/* List View: Students with Assignments */}
      {view === "list" && (
        <Card>
          {/* Header */}
          <div style={{ marginBottom: 24 }}>
            <Title level={3} style={{ marginBottom: 8 }}>
              <ShoppingOutlined style={{ marginRight: 8 }} />
              BOT Clearance Portal
            </Title>
            <Text type="secondary">
              Click on a student to view and manage their BOT clearance
              assignments.
            </Text>
          </div>

          {/* Assignments Table */}
          <AssignmentsTable onSelectAssignment={handleSelectAssignment} />
        </Card>
      )}

      {/* Wizard View: Multi-step Clearance Process */}
      {view === "wizard" && selectedAssignment && (
        <ClearanceWizard
          assignment={selectedAssignment}
          onBack={handleBackToList}
        />
      )}
    </div>
  );
};

export default BotClearance;
